import React, { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormikContext } from "formik";
import { Box, Text, Stack, Checkbox, Divider } from "@chakra-ui/react";

import { AddressWithPlacesAutocomplete } from "components/Form";
import { isValidCountryIso } from "utils/regions";

const PERMANENT_ADDRESS_FORM_NAMES = {
  street: "contact.street",
  street2: "contact.street2",
  zip: "contact.zip",
  city: "contact.city",
  state: "contact.state",
  country: "contact.country",
  googlePlacesAutocomplete: "contact.googlePlacesAutocomplete",
};

const MAILING_ADDRESS_FORM_ROOT = "contact.mailingAddress";
const permanentAddressGooglePlaceFormattedAddress = "contact.google_place_formatted_address";
const mailingAddressGooglePlaceFormattedAddress = `${MAILING_ADDRESS_FORM_ROOT}.google_place_formatted_address`;

const MAILING_ADDRESS_FORM_NAMES = {
  street: `${MAILING_ADDRESS_FORM_ROOT}.street`,
  street2: `${MAILING_ADDRESS_FORM_ROOT}.street2`,
  zip: `${MAILING_ADDRESS_FORM_ROOT}.zip`,
  city: `${MAILING_ADDRESS_FORM_ROOT}.city`,
  state: `${MAILING_ADDRESS_FORM_ROOT}.state`,
  country: `${MAILING_ADDRESS_FORM_ROOT}.country`,
  googlePlacesAutocomplete: `${MAILING_ADDRESS_FORM_ROOT}.googlePlacesAutocomplete`,
};

export default function KYCFirstHalfAddressSection({ country }) {
  const { formatMessage } = useIntl();
  const {
    setFieldValue,
    values: {
      contact: { useForMailingAddress },
    },
  } = useFormikContext();

  const googlePlaceCountryParam = isValidCountryIso(country) ? country : "";

  const onCheckboxChange = useCallback(
    (e) => {
      setFieldValue(MAILING_ADDRESS_FORM_ROOT, {
        street: "",
        street2: "",
        city: "",
        state: "",
        zip: "",
        country: "",
      });
      setFieldValue("contact.useForMailingAddress", e.target.checked);
    },
    [setFieldValue]
  );

  const onPermanentAddressPlaceChange = useCallback(
    ({ formatted_address }) => {
      setFieldValue(permanentAddressGooglePlaceFormattedAddress, formatted_address);
    },
    [setFieldValue]
  );

  const onMailingAddressPlaceChange = useCallback(
    ({ formatted_address }) => {
      setFieldValue(mailingAddressGooglePlaceFormattedAddress, formatted_address);
    },
    [setFieldValue]
  );

  return (
    <Stack spacing={6}>
      <AddressWithPlacesAutocomplete
        label={formatMessage({
          defaultMessage: "Permanent Residence Address",
          description: "Input label",
        })}
        country={googlePlaceCountryParam}
        names={PERMANENT_ADDRESS_FORM_NAMES}
        onPlaceChange={onPermanentAddressPlaceChange}
      />
      <Checkbox
        data-testid="use-mailing-address-checkbox"
        isChecked={useForMailingAddress}
        onChange={onCheckboxChange}
      >
        <FormattedMessage
          defaultMessage="Use this for mailing address"
          description="Call to action description"
        />
      </Checkbox>
      <Divider />
      <Box>
        <Stack spacing={4}>
          <Text>
            <FormattedMessage
              defaultMessage="We will use this address for tax purposes if your annual earnings from this service exceed the IRS threshold for non-employment income."
              description="Informative message"
            />
          </Text>
        </Stack>
        {!useForMailingAddress && (
          <Box marginTop="40px">
            <AddressWithPlacesAutocomplete
              label={formatMessage({
                defaultMessage: "Mailing Address",
                description: "Input label",
              })}
              names={MAILING_ADDRESS_FORM_NAMES}
              onPlaceChange={onMailingAddressPlaceChange}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}
